import * as React from "react";
import { useCombobox } from "downshift";
import styled, { css } from "styled-components";
import { useDebounce } from "@gravitywelluk/react-hooks";
import _ from "lodash";

import useOutSideItemClick from "src/library/hooks/useOutsideItemClick";
import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { InputCss } from "src/library/components/Forms/Input";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { IDrop } from "src/core/drops/api/drops.api";

interface PaymentMethodBranchAssignmentSearchProps {
  items: IDrop[];
  onFetch: (searchTerm: string) => void
  onSelected: (drop: IDrop) => void;
  label?: string;
  loading?: boolean;
}

/**
 * Payment method branch assignment search component
 */
const PaymentMethodBranchAssignmentSearch: React.ReactFCWithChildren<PaymentMethodBranchAssignmentSearchProps> = props => {
  const [ searchItems, setSearchItems ] = React.useState<IDrop[]>([]);
  const [ searchInitialized, setSearchInitialized ] = React.useState(false);
  const [ value, setValue ] = React.useState("");
  const debouncedInputValue = useDebounce<string>(value, 1500);
  const [ isOpen, setIsOpen ] = React.useState<boolean>(false);

  const {
    highlightedIndex,
    selectedItem,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    setInputValue
  } = useCombobox(
    {
      isOpen,
      // The search items narrowed down
      items: searchItems,
      // Input change functionality
      onInputValueChange: ({ inputValue }) => {
        const parsedInputValue = inputValue || "" ;
        const foundNameItems = props.items.filter(item => item.name.toLowerCase().includes(parsedInputValue.toLowerCase()));
        const foundBranchNumberItems = props.items.filter(item => item.branchNumber.toLowerCase().includes(parsedInputValue.toLowerCase()));
        let foundItems: IDrop[] = [];

        // Merge the found names and branchNumbers together and make sure they are unique
        foundItems = _.uniqWith<IDrop>(foundItems.concat(foundNameItems, foundBranchNumberItems), (a, b) => a.id === b.id);
        // Update the searchItems state
        setSearchItems(foundItems);
        setIsOpen(true);
        // Update the value state
        setValue(parsedInputValue);
      }
    });

  // On debouncedInputValue change
  React.useEffect(() => {
    // If the search has no yet been initialized, update the searchInitialized state
    if (!searchInitialized && debouncedInputValue.length > 0) {
      // Set the searchInitialized state
      setSearchInitialized(true);
      // Fetch
      props.onFetch(debouncedInputValue);
    }

    if (searchInitialized) {
      props.onFetch(debouncedInputValue);
    }
  // Ignore props
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ debouncedInputValue ]);

  // On props.items change
  React.useEffect(() => {
    // Update the searchItems state
    setSearchItems(props.items);
  }, [ props.items ]);

  // On selectedItem change, call the given onSelected function
  React.useEffect(() => {
    // If selectedItem exists
    if (selectedItem) {
      // Call the given onSelected function
      props.onSelected(selectedItem);
      // Reset the input value
      setInputValue("");
    }
  // Ignore props
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ selectedItem ]);

  const searchRef = React.useRef<HTMLDivElement>(null);

  useOutSideItemClick(searchRef, () => {
    setIsOpen(false);
  });

  return (
    <StyledPaymentMethodBranchAssignmentSearch ref={searchRef}>
      {props.label && (
        <Text
          as="label"
          {...getLabelProps()}
        >
          {props.label}
        </Text>
      )}

      <div
        className="input-container"
      >
        <div className="input-wrapper">
          <input
            {...getInputProps({}, { suppressRefError: true })}
          />

          <Icon
            className="icon"
            icon={Icons.SEARCH}
            $fillColor="darkGrey"
          />
        </div>

        {props.loading && (
          <LoadingSpinner
            type="flat"
            position="inline"
            size={8}
          />
        )}
      </div>

      <ul
        className={`results-wrapper ${searchItems.length === 0 ? "empty" : ""} ${isOpen ? "open" : "hide"}`}
        {...getMenuProps({}, { suppressRefError: true })}
      >
        {searchItems.length > 0 && searchItems.map((item, index) => (
          <li
            key={item.id}
            {...getItemProps({
              item,
              index
            })}
            className={highlightedIndex === index ? "highlighted" : ""}
          >
            <Text className="branch-name">
              {item.name}
            </Text>

            <Text className="card-details">
              {item.name}
            </Text>
          </li>
        ))}
      </ul>
    </StyledPaymentMethodBranchAssignmentSearch>
  );
};

export default PaymentMethodBranchAssignmentSearch;

const StyledPaymentMethodBranchAssignmentSearch = styled.div`
  ${({ theme }) => css`
    label {
      display: block;
      padding-bottom: 0.6rem;
    }

    .input-container {
      display: flex;
      align-items: center;

      .input-wrapper {
        position: relative;

        input {
          ${InputCss};
          padding-left: 4.4rem;
          width: auto;
          min-width: 24rem;
          max-width: 40rem;
          border: 0.2rem solid ${theme.colors.lightGrey};
          border-radius: 0.4rem;
        }

        .icon {
          position: absolute;
          z-index: 2;
          top: 50%;
          left: 1.4rem;
          transform: translateY(-50%);
        }
      }
    }

    .results-wrapper {
      max-width: 32rem;
      border: 0.2rem solid ${theme.colors.darkGrey};
      border-radius: 0.4rem;

      &.hide {
        display: none;
      }

      &.empty {
        border-color: transparent;
      }

      li {
        transition: ${theme.transition.quickest};
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.6rem 1rem;
        cursor: pointer;

        &:nth-child(even) {
          background-color: ${theme.colors.lightestGrey};
        }

        &.highlighted {
          background-color: ${theme.colors.lightGrey};
        }

        .branch-name {
          font-size: ${theme.fonts.sizes.h4};
        }

        .card-details {
          color: ${theme.fonts.colors.secondary};
          font-size: ${theme.fonts.sizes.h5};
        }
      }
    }
  `};
`;
